import { clampRect } from '@/core/geometry'
import { recognitionMetricKeys, type CaptureProfile, type LocatedMetricRegion, type NormalizedRect, type RecognitionMetricKey } from '@/domain/types'
import { drawSourceToImageData, isImageDataSource } from '@/core/image'
import type { LayoutDetection, LayoutFailureReason, LayoutSearchOptions } from '../types'
import { compactTextRect, COMPACT_HUD, HUD_STRUCTURE } from './layout-regions'
import templates from './layout-templates.json'
import compactTemplates from './compact-layout-templates.json'
import { mapPanelRight } from './map-line'

interface Run { x: number; width: number }
interface Template { width: number; height: number; pixels: number[][] }
interface Anchor { x: number; y: number; sx: number; sy: number; error: number; hudVariant?: 'wide' | 'compact' }
const hudMetrics = ['level', 'jobName', 'hp', 'mp', 'exp'] as const
const labels = [templates.hp, templates.mp, templates.exp]
const compactLabels = [compactTemplates.hp, compactTemplates.mp, compactTemplates.exp]
const offsets = [0, 168, 344]
const thresholds = [175, 140, 110, 205]

function normalized(frame: ImageData, x: number, y: number, width: number, height: number): NormalizedRect {
  return clampRect({ x: x / frame.width, y: y / frame.height, width: width / frame.width, height: height / frame.height })
}
function inside(f: ImageData, x: number, y: number, w: number, h: number) {
  return x >= 0 && y >= 0 && x + w <= f.width + 1 && y + h <= f.height + 1
}
function gray(data: ArrayLike<number>, i: number) { return (data[i]! + data[i + 1]! * 2 + data[i + 2]!) / 4 }
const templateIds = new WeakMap<object, number>()
let nextTemplateId = 0
const resized = new Map<string, { w: number; h: number; rgb: number[] }>()
/** Resample the template, rather than sampling isolated source pixels after scaling. */
function scaled(t: Template, sx: number, sy: number, nearest = 0) {
  const w = Math.max(3, Math.round(t.width * sx)), h = Math.max(3, Math.round(t.height * sy))
  if (!templateIds.has(t)) templateIds.set(t, ++nextTemplateId)
  const key = `${templateIds.get(t)},${w},${h},${nearest}`
  const cached = resized.get(key)
  if (cached) return cached
  const rgb: number[] = []
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const tx = Math.max(0, (x + .5) * t.width / w - .5), ty = Math.max(0, (y + .5) * t.height / h - .5)
    const x0 = Math.floor(tx), y0 = Math.floor(ty), x1 = Math.min(t.width - 1, x0 + 1), y1 = Math.min(t.height - 1, y0 + 1)
    if (nearest) {
      const nx = Math.max(0, Math.min(t.width - 1, Math.floor((x + .5) / sx - .5 + ((nearest - 1) % 3) * .5))), ny = Math.max(0, Math.min(t.height - 1, Math.floor((y + .5) / sy - .5 + Math.floor((nearest - 1) / 3) * .5)))
      for (let c = 2; c < 5; c++) rgb.push(t.pixels[ny * t.width + nx]![c]!)
      continue
    }
    for (let c = 2; c < 5; c++) rgb.push(
      (1 - (ty - y0)) * ((1 - (tx - x0)) * t.pixels[y0 * t.width + x0]![c]! + (tx - x0) * t.pixels[y0 * t.width + x1]![c]!)
      + (ty - y0) * ((1 - (tx - x0)) * t.pixels[y1 * t.width + x0]![c]! + (tx - x0) * t.pixels[y1 * t.width + x1]![c]!))
  }
  const value = { w, h, rgb }
  if (resized.size > 512) resized.clear()
  resized.set(key, value)
  return value
}
function patchError(f: ImageData, t: Template, x: number, y: number, sx: number, sy: number): number {
  const smooth = patchVariantError(f, t, x, y, sx, sy, 0)
  if (smooth < 35 || sx >= 1 && sy >= 1) return smooth
  let best = smooth
  for (let variant = 1; variant <= 9; variant++) {
    best = Math.min(best, patchVariantError(f, t, x, y, sx, sy, variant))
    if (best < 25) break
  }
  return best
}
function patchVariantError(f: ImageData, t: Template, x: number, y: number, sx: number, sy: number, nearest: number): number {
  const a = scaled(t, sx, sy, nearest), left = Math.round(x), top = Math.round(y)
  if (!inside(f, left, top, a.w, a.h)) return 255
  let error = 0, sumA = 0, sumB = 0, aa = 0, bb = 0, ab = 0, n = 0
  for (let dy = 0; dy < a.h; dy++) for (let dx = 0; dx < a.w; dx++) {
    const i = ((top + dy) * f.width + left + dx) * 4, j = (dy * a.w + dx) * 3
    const av = (a.rgb[j]! + a.rgb[j + 1]! * 2 + a.rgb[j + 2]!) / 4, bv = gray(f.data, i)
    for (let c = 0; c < 3; c++) error += Math.abs(a.rgb[j + c]! - f.data[i + c]!) / 3
    sumA += av; sumB += bv; aa += av * av; bb += bv * bv; ab += av * bv; n++
    if (n >= 12 && error / n > 85) return 255
  }
  const variance = Math.sqrt(Math.max(0, aa - sumA * sumA / n) * Math.max(0, bb - sumB * sumB / n))
  const correlation = variance > 1 ? (ab - sumA * sumB / n) / variance : 0
  if (correlation < .15) return 255
  return error / n * .6 + (1 - correlation) * 35
}
function refined(f: ImageData, t: Template, x: number, y: number, sx: number, sy: number, radius = 1) {
  let best = 255
  for (let dy = -radius; dy <= radius; dy++) for (let dx = -radius; dx <= radius; dx++) best = Math.min(best, patchError(f, t, x + dx, y + dy, sx, sy))
  return best
}
function lightRuns(f: ImageData, y: number, threshold: number, min = 70, max = 350): Run[] {
  const runs: Run[] = []; let start = -1
  for (let x = 0; x <= f.width; x++) {
    const i = (y * f.width + x) * 4, r = f.data[i] ?? 0, g = f.data[i + 1] ?? 0, b = f.data[i + 2] ?? 0
    const light = x < f.width && (r + g * 2 + b) / 4 > threshold
    if (light && start < 0) start = x
    if (!light && start >= 0) { const width = x - start; if (width >= min && width <= max) runs.push({ x: start, width }); start = -1 }
  }
  return runs
}
function borderRow(f: ImageData, x: number, y: number, width: number) {
  let count = 0
  for (let dx = 2; dx < width - 2; dx++) {
    const i = (Math.round(y) * f.width + Math.round(x + dx)) * 4
    if (gray(f.data, i) > 115) count++
  }
  return count / Math.max(1, width - 4) > .78
}
function border(f: ImageData, x: number, y: number, width: number) {
  return [-1, 0, 1].some(dy => borderRow(f, x, y + dy, width))
}
function hudFields(f: ImageData, a: Anchor): LayoutDetection {
  if (a.hudVariant === 'compact') return compactHudFields(f, a)
  const { x, y, sx, sy } = a, left = x - 465 * sx, top = y - 17 * sy
  const result: LayoutDetection = { hud: normalized(f, left, top, 1364 * sx, 39 * sy), regions: {} }
  for (const [i, metric] of (['hp', 'mp', 'exp'] as const).entries()) {
    const bx = x + offsets[i]! * sx
    if (!inside(f, bx - sx, y - 14 * sy, 167 * sx, 32 * sy)) continue
    const topBorder = border(f, bx, y, 165 * sx), bottomBorder = border(f, bx, y + 17 * sy, 165 * sx)
    if (refined(f, labels[i]!, bx - sx, y - 13 * sy, sx, sy) > 48 || (sy < 1 ? !topBorder && !bottomBorder : !topBorder || !bottomBorder)) continue
    const rect = normalized(f, bx - sx, y - 14 * sy, 167 * sx, 32 * sy)
    const barRect = normalized(f, bx, y + 2 * sy, 165 * sx, 14 * sy)
    result.regions[metric] = {
      rect, barRect, textRect: compactTextRect(metric, rect, barRect),
      confidence: Math.max(.7, 1 - a.error / 150),
    }
  }
  // The name panel has a fixed neutral background; do not infer fields behind overlays.
  for (const metric of ['level', 'jobName'] as const) {
    const box = HUD_STRUCTURE[metric]
    const px = left + box.x * sx, py = top + box.y * sy
    if (!inside(f, px, py, box.width * sx, box.height * sy)) continue
    let neutral = 0, n = 0
    for (let yy = Math.round(py); yy < py + box.height * sy; yy += 2) for (let xx = Math.round(px); xx < px + box.width * sx; xx += 2) {
      const i = (yy * f.width + xx) * 4, r = f.data[i]!, g = f.data[i + 1]!, b = f.data[i + 2]!
      if (Math.max(r, g, b) - Math.min(r, g, b) < 65 && r > 15 && r < 150) neutral++
      n++
    }
    if (neutral / n > .55) {
      const rect = normalized(f, px, py, box.width * sx, box.height * sy)
      result.regions[metric] = { rect, textRect: rect, confidence: Math.max(.7, 1 - a.error / 150) }
    }
  }
  return result
}

function compactHudFields(f: ImageData, a: Anchor): LayoutDetection {
  const { x, y, sx, sy } = a
  const left = x - COMPACT_HUD.anchorX * sx, top = y - COMPACT_HUD.anchorY * sy
  const result: LayoutDetection = { hud: normalized(f, left, top, COMPACT_HUD.width * sx, COMPACT_HUD.height * sy), regions: {} }
  for (const [i, metric] of (['hp', 'mp', 'exp'] as const).entries()) {
    const bx = x + COMPACT_HUD.offsets[i]! * sx, width = COMPACT_HUD.widths[i]!
    if (!inside(f, bx - 2 * sx, y - 15 * sy, (width + 3) * sx, 32 * sy)) continue
    if (!compactAnchorMatches(f, a, i, 48)) continue
    const rect = normalized(f, bx - 2 * sx, y - 15 * sy, (width + (i === 2 ? 3 : 4)) * sx, 32 * sy)
    const barRect = normalized(f, bx, y + 2 * sy, width * sx, COMPACT_HUD.barHeight * sy)
    result.regions[metric] = { hudVariant: 'compact', rect, barRect,
      textRect: normalized(f, bx - 2 * sx, y - 15 * sy, (width + (i === 2 ? 3 : 4)) * sx, 15 * sy),
      confidence: Math.max(.7, 1 - a.error / 150) }
  }
  for (const metric of ['level', 'jobName'] as const) {
    const box = COMPACT_HUD.structure[metric], px = left + box.x * sx, py = top + box.y * sy
    if (!inside(f, px, py, box.width * sx, box.height * sy)) continue
    let neutral = 0, n = 0
    for (let yy = Math.round(py); yy < py + box.height * sy; yy += 2) for (let xx = Math.round(px); xx < px + box.width * sx; xx += 2) {
      const i = (yy * f.width + xx) * 4, r = f.data[i]!, g = f.data[i + 1]!, b = f.data[i + 2]!
      if (Math.max(r, g, b) - Math.min(r, g, b) < 65 && r > 15 && r < 150) neutral++
      n++
    }
    if (neutral / n > .55) {
      const rect = normalized(f, px, py, box.width * sx, box.height * sy)
      result.regions[metric] = { hudVariant: 'compact', rect, textRect: rect, confidence: Math.max(.7, 1 - a.error / 150) }
    }
  }
  return result
}

function compactAnchorMatches(f: ImageData, a: Anchor, i: number, tolerance = 46): boolean {
  const bx = a.x + COMPACT_HUD.offsets[i]! * a.sx, width = COMPACT_HUD.widths[i]! * a.sx
  return border(f, bx, a.y, width) && border(f, bx, a.y + COMPACT_HUD.borderHeight * a.sy, width)
    && refined(f, compactLabels[i]!, bx - 2 * a.sx, a.y - 13 * a.sy, a.sx, a.sy) <= tolerance
}

/** Decode persisted geometry using its own layout, including partial HUD groups. */
function regionAnchor(region: LocatedMetricRegion, index: number, width: number, height: number): Anchor {
  const bar = region.barRect!, compact = region.hudVariant === 'compact'
  const sx = bar.width * width / (compact ? COMPACT_HUD.widths[index]! : 165)
  const sy = bar.height * height / (compact ? COMPACT_HUD.barHeight : 14)
  return { x: bar.x * width - (compact ? COMPACT_HUD.offsets[index]! : offsets[index]!) * sx,
    y: bar.y * height - 2 * sy, sx, sy, error: 0, hudVariant: region.hudVariant }
}

/** Check the saved label and track, never trust a color patch alone. */
export function validMxdcBar(source: CanvasImageSource | ImageData, width: number, height: number,
  metric: 'hp' | 'mp' | 'exp', region: LocatedMetricRegion): boolean {
  if (!region.barRect) return false
  const index = ['hp', 'mp', 'exp'].indexOf(metric)
  let a = regionAnchor(region, index, width, height)
  const f = isImageDataSource(source) ? source : drawSourceToImageData(source, width, height, region.rect)
  if (!isImageDataSource(source)) a = { ...a, x: a.x - Math.round(region.rect.x * width), y: a.y - Math.round(region.rect.y * height) }
  if (a.hudVariant === 'compact') return compactAnchorMatches(f, a, index, 50)
  const bx = a.x + offsets[index]! * a.sx
  const top = border(f, bx, a.y, 165 * a.sx), bottom = border(f, bx, a.y + 17 * a.sy, 165 * a.sx)
  return refined(f, labels[index]!, bx - a.sx, a.y - 13 * a.sy, a.sx, a.sy, 2) < 50
    && (a.sy < 1 ? top || bottom : top && bottom)
}

/** A manual job crop still needs structural evidence to distinguish a nickname. */
export function locatedJobRect(profile: CaptureProfile, width: number, height: number,
  anchorMetric?: 'hp' | 'mp' | 'exp'): NormalizedRect | undefined {
  const saved = profile.autoRois
  const index = (['hp', 'mp', 'exp'] as const).findIndex(metric => saved?.[metric]?.barRect && (!anchorMetric || metric === anchorMetric))
  if (index < 0) return saved?.jobName?.rect
  const region = saved![(['hp', 'mp', 'exp'] as const)[index]!]!
  const a = regionAnchor(region, index, width, height), compact = a.hudVariant === 'compact'
  const box = compact ? COMPACT_HUD.structure.jobName : HUD_STRUCTURE.jobName
  return { x: (a.x - (compact ? COMPACT_HUD.anchorX : 465) * a.sx + box.x * a.sx) / width,
    y: (a.y - (compact ? COMPACT_HUD.anchorY : 17) * a.sy + box.y * a.sy) / height,
    width: box.width * a.sx / width, height: box.height * a.sy / height }
}

function* compactHudCandidates(f: ImageData): Generator<void, Anchor[]> {
  const anchors: Anchor[] = [], seen = new Set<string>()
  for (const threshold of thresholds) for (let y = 0; y < f.height - 7; y++) {
    const runs = lightRuns(f, y, threshold, 45)
    for (let i = 0; i < runs.length; i++) for (let j = i + 1; j < runs.length; j++) {
      const a = runs[i]!, b = runs[j]!
      if (b.x - a.x > 710) break
      for (const [ia, ib] of [[0, 1], [1, 2], [0, 2]] as const) {
        const sx = (b.x - a.x) / (COMPACT_HUD.offsets[ib] - COMPACT_HUD.offsets[ia])
        if (sx < .46 || sx > 2.06 || Math.abs(a.width - COMPACT_HUD.widths[ia] * sx) > 4
          || Math.abs(b.width - COMPACT_HUD.widths[ib] * sx) > 4) continue
        const x = a.x - COMPACT_HUD.offsets[ia] * sx, key = `${Math.round(x)},${y},${Math.round(sx * 100)}`
        if (seen.has(key)) continue
        seen.add(key)
        for (let height = 7; height <= 32; height++) {
          const measured = height / COMPACT_HUD.borderHeight, sy = Math.abs(measured - sx) < .045 ? sx : measured
          if (y + height >= f.height) continue
          const candidate: Anchor = { x, y, sx, sy, error: 0, hudVariant: 'compact' }
          if (!compactAnchorMatches(f, candidate, ia) || !compactAnchorMatches(f, candidate, ib)) continue
          const error = (refined(f, compactLabels[ia]!, a.x - 2 * sx, y - 13 * sy, sx, sy)
            + refined(f, compactLabels[ib]!, b.x - 2 * sx, y - 13 * sy, sx, sy)) / 2
            + (Math.abs(a.width - COMPACT_HUD.widths[ia] * sx) + Math.abs(b.width - COMPACT_HUD.widths[ib] * sx)) * 10
            + Math.min(3, Math.abs(sy - sx) * 25)
          const existing = anchors.find(v => Math.abs(v.x - x) < 8 && Math.abs(v.y - y) < 8)
          if (!existing) anchors.push({ ...candidate, error })
          else if (error < existing.error) Object.assign(existing, candidate, { error })
        }
      }
    }
    yield
  }
  return anchors
}

/** One octave of the image pyramid; pixel centers stay aligned when mapping back. */
function* halfImage(f: ImageData): Generator<void, ImageData> {
  const width = Math.ceil(f.width / 2), height = Math.ceil(f.height / 2)
  const data = new Uint8ClampedArray(width * height * 4)
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) for (let c = 0; c < 4; c++) {
      let value = 0
      for (let dy = 0; dy < 2; dy++) for (let dx = 0; dx < 2; dx++) value += f.data[(Math.min(f.height - 1, y * 2 + dy) * f.width + Math.min(f.width - 1, x * 2 + dx)) * 4 + c]!
      data[(y * width + x) * 4 + c] = value / 4
    }
    yield
  }
  return { width, height, data } as ImageData
}
function mapField(f: ImageData, map: Anchor) {
    const left = map.x - 5 * map.sx, top = map.y - 4 * map.sy, start = Math.round(left + 48 * map.sx)
    const right = mapPanelRight(f, { x: start, y: Math.round(top + 43 * map.sy),
      width: Math.round(402 * map.sx), height: Math.round(18 * map.sy) })
    if (right - start > 20 * map.sx) {
      const rect = normalized(f, start, top + 43 * map.sy, right - start, 18 * map.sy)
      return { rect, textRect: rect, confidence: Math.max(.7, 1 - map.error / 100) }
    }
}

/** Repair an old automatic text crop from its saved minimap anchor, without changing calibration. */
export function repairAutomaticMapRect(source: CanvasImageSource | ImageData, width: number, height: number, profile: CaptureProfile): NormalizedRect | null {
  const saved = profile.autoRois?.mapName?.rect
  if (!saved || profile.roiOverrides.mapName) return null
  const index = (['hp', 'mp', 'exp'] as const).findIndex(metric => profile.autoRois?.[metric]?.barRect)
  const sy = saved.height * height / 18, sx = index >= 0
    ? regionAnchor(profile.autoRois![(['hp', 'mp', 'exp'] as const)[index]!]!, index, width, height).sx : sy
  const x = Math.round(saved.x * width - 43 * sx), y = Math.round(saved.y * height - 39 * sy)
  if (x < 0 || y < 0 || sx <= 0 || sy <= 0) return null
  const bounds = clampRect({ x: x / width, y: y / height, width: 446 * sx / width, height: 58 * sy / height })
  const image = isImageDataSource(source) ? source : drawSourceToImageData(source, width, height, bounds)
  const anchorX = isImageDataSource(source) ? x : 0, anchorY = isImageDataSource(source) ? y : 0
  const error = patchError(image, templates.minimap, anchorX, anchorY, sx, sy)
  if (error > 43) return null
  const region = mapField(image, { x: anchorX, y: anchorY, sx, sy, error })?.rect
  if (!region) return null
  return isImageDataSource(source) ? region : clampRect({
    x: (x + region.x * image.width) / width, y: (y + region.y * image.height) / height,
    width: region.width * image.width / width, height: region.height * image.height / height,
  })
}

/** Scan one pyramid level. All candidates are mapped and checked against source pixels. */
function* hudCandidates(f: ImageData): Generator<void, { anchors: Anchor[]; undersized: boolean }> {
  const hud: Anchor[] = [], seen = new Set<string>()
  let undersized = false
  for (const threshold of thresholds) {
    for (let y = 0; y < f.height - 7; y++) {
      const runs = lightRuns(f, y, threshold)
      for (let i = 0; i < runs.length; i++) for (let j = i + 1; j < runs.length; j++) {
        const a = runs[i]!, b = runs[j]!
        if (b.x - a.x > 710) break
        if (Math.abs(a.width - b.width) > 5) continue
        for (const [ia, ib] of [[0, 1], [1, 2], [0, 2]]) {
          const sx = (b.x - a.x) / (offsets[ib!]! - offsets[ia!]!)
          if (Math.abs(a.width - 165 * sx) > 4 || sx > 2.06) continue
          if (sx < .46) {
            if (sx >= .2 && refined(f, labels[ia!]!, a.x - sx, y - 13 * sx, sx, sx) < 40
              && refined(f, labels[ib!]!, b.x - sx, y - 13 * sx, sx, sx) < 40) undersized = true
            continue
          }
          const x = a.x - offsets[ia!]! * sx, key = `${Math.round(x)},${y},${Math.round(sx * 100)}`
          if (seen.has(key)) continue
          seen.add(key)
          for (let height = 8; height <= 35; height++) {
            const measured = height / 17
            const sy = Math.abs(measured - sx) < .045 ? sx : measured
            if (y + height >= f.height || !border(f, a.x, y + height, a.width) || !border(f, b.x, y + height, b.width)) continue
            const e1 = refined(f, labels[ia!]!, a.x - sx, y - 13 * sy, sx, sy)
            if (e1 > 46) continue
            const e2 = refined(f, labels[ib!]!, b.x - sx, y - 13 * sy, sx, sy)
            if (e2 > 46) continue
            const widthError = Math.abs(a.width - 165 * sx) + Math.abs(b.width - 165 * sx)
            const error = (e1 + e2) / 2 + widthError * 10 + Math.min(3, Math.abs(sy - sx) * 25)
            const existing = hud.find(v => Math.abs(v.x - x) < 8 && Math.abs(v.y - y) < 8)
            if (!existing) hud.push({ x, y, sx, sy, error })
            else if (error < existing.error) Object.assign(existing, { x, y, sx, sy, error })
          }
        }
      }
      yield
    }
  }
  return { anchors: hud, undersized }
}
function refineHud(f: ImageData, candidate: Anchor, radius: number): Anchor | null {
  let best: Anchor | null = null
  for (let dy = -radius; dy <= radius; dy++) for (let dx = -radius; dx <= radius; dx++) {
    const a = { ...candidate, x: candidate.x + dx, y: candidate.y + dy }
    if (a.hudVariant === 'compact') {
      const matched = [0, 1, 2].filter(i => compactAnchorMatches(f, a, i))
      if (matched.length < 2) continue
      const error = matched.reduce((sum, i) => sum + refined(f, compactLabels[i]!, a.x + COMPACT_HUD.offsets[i]! * a.sx - 2 * a.sx, a.y - 13 * a.sy, a.sx, a.sy), 0) / matched.length
        + (Math.abs(dx) + Math.abs(dy)) * .05
      if (!best || error < best.error) best = { ...a, error }
      continue
    }
    const errors = labels.map((label, i) => refined(f, label!, a.x + offsets[i]! * a.sx - a.sx, a.y - 13 * a.sy, a.sx, a.sy))
      .sort((a, b) => a - b)
    if (errors[1]! > 46 || [0, 1, 2].filter(i => border(f, a.x + offsets[i]! * a.sx, a.y, 165 * a.sx)
      && border(f, a.x + offsets[i]! * a.sx, a.y + 17 * a.sy, 165 * a.sx)).length < 2) continue
    const error = (errors[0]! + errors[1]!) / 2 + (Math.abs(dx) + Math.abs(dy)) * .05
    if (!best || error < best.error) best = { ...a, error }
  }
  return best
}
function searchCancelled(options: LayoutSearchOptions): void {
  if (options.signal?.aborted) throw new DOMException('Calibration cancelled', 'AbortError')
  if (options.deadlineAt !== undefined && Date.now() >= options.deadlineAt) throw new DOMException('Calibration timed out', 'TimeoutError')
}

/** Yield row-sized work units so the same implementation can run cooperatively without a Worker. */
export function* layoutSearch(f: ImageData, metrics: readonly RecognitionMetricKey[] = recognitionMetricKeys, options: LayoutSearchOptions = {}): Generator<void, LayoutDetection> {
  const started = performance.now(), hud: Anchor[] = []
  const mesosOnly = metrics.length === 1 && metrics[0] === 'mesos'
  let undersized = false
  const hint = options.hint ? validateMxdcLayout(f, options.hint) : undefined
  const hintMetric = (['hp', 'mp', 'exp'] as const).find(metric => hint?.regions[metric]?.barRect)
  const hintBar = hintMetric ? hint?.regions[hintMetric]?.barRect : undefined
  if (!mesosOnly && (metrics.some(metric => hudMetrics.includes(metric as typeof hudMetrics[number])) || !hintBar)) {
    let level = f
    for (const factor of [1, 2, 4]) {
      if (factor > 1) level = yield* halfImage(level)
      const search = hudCandidates(level)
      let step = search.next()
      while (!step.done) { searchCancelled(options); yield; step = search.next() }
      undersized ||= step.value.undersized
      for (const candidate of step.value.anchors) {
        const mapped = { ...candidate, x: candidate.x * factor, y: candidate.y * factor, sx: candidate.sx * factor, sy: candidate.sy * factor }
        if (mapped.sx > 4.06 || mapped.sy > 4.06) continue
        // Prefer source-resolution geometry to a resampled duplicate.
        if (hud.some(a => Math.abs(a.x - mapped.x) < 8 * Math.max(a.sx, mapped.sx)
          && Math.abs(a.y - mapped.y) < 8 * Math.max(a.sy, mapped.sy))) continue
        const refined = factor === 1 ? mapped : refineHud(f, mapped, factor)
        if (!refined) continue
        const existing = hud.find(a => Math.abs(a.x - refined.x) < 8 * Math.max(a.sx, refined.sx)
          && Math.abs(a.y - refined.y) < 8 * Math.max(a.sy, refined.sy))
        if (!existing) hud.push(refined)
        else if (refined.error < existing.error) Object.assign(existing, refined)
      }
      searchCancelled(options)
    }
    // Keep the original successful path and ambiguity decisions unchanged.
    if (hud.length === 0) {
      let compactLevel = f
      for (const factor of [1, 2, 4]) {
        if (factor > 1) compactLevel = yield* halfImage(compactLevel)
        const search = compactHudCandidates(compactLevel)
        let step = search.next()
        while (!step.done) { searchCancelled(options); yield; step = search.next() }
        for (const candidate of step.value) {
          const mapped = { ...candidate, x: candidate.x * factor, y: candidate.y * factor, sx: candidate.sx * factor, sy: candidate.sy * factor }
          if (mapped.sx > 4.06 || mapped.sy > 4.06) continue
          if (hud.some(a => Math.abs(a.x - mapped.x) < 8 * Math.max(a.sx, mapped.sx)
            && Math.abs(a.y - mapped.y) < 8 * Math.max(a.sy, mapped.sy))) continue
          const refined = factor === 1 ? mapped : refineHud(f, mapped, factor)
          if (refined) hud.push(refined)
        }
        searchCancelled(options)
      }
    }
  }
  hud.sort((a, b) => a.error - b.error)
  if (hud.length > 1) return { regions: {}, diagnostics: { reason: 'multiple-scenes', durationMs: performance.now() - started, candidates: hud.length } }
  const best = hud[0] ?? (hintBar && hintMetric ? regionAnchor(hint!.regions[hintMetric]!, ['hp', 'mp', 'exp'].indexOf(hintMetric), f.width, f.height) : undefined)
  const hudSearchMs = performance.now() - started
  const result: LayoutDetection = hud[0] ? hudFields(f, hud[0]) : { regions: {} }
  const sx = best?.sx ?? 1, sy = best?.sy ?? 1
  const maps: Anchor[] = [], coins: Anchor[] = []
  // Large headers use the matching pyramid octave before source-pixel refinement.
  const mapStarted = performance.now()
  const searchScales = best ? [[sx, sy]] : [.5, .625, .75, .8, 1, 1.25, 1.5, 2, 2.25, 2.5, 3, 4].map(v => [v, v])
  let half: ImageData | undefined, quarter: ImageData | undefined
  if (metrics.includes('mapName')) for (const [mx, my] of searchScales) {
    const factor = mx! >= 3 && my! >= 3 ? 4 : mx! >= 1.5 && my! >= 1.5 ? 2 : 1
    if (factor >= 2 && !half) half = yield* halfImage(f)
    if (factor === 4 && !quarter) quarter = yield* halfImage(half!)
    const coarse = factor === 4 ? quarter! : factor === 2 ? half! : f
    for (let y = 0; y < coarse.height - 20 * my! / factor; y++) {
      for (let x = 0; x < coarse.width - 45 * mx! / factor; x++) {
        const i = (y * coarse.width + x) * 4
        if (coarse.data[i]! < 80 || coarse.data[i + 2]! < coarse.data[i]! + 6 || coarse.data[i + 1]! < coarse.data[i]! + 2) continue
        if (factor >= 2 && patchVariantError(coarse, templates.minimap, x, y, mx! / factor, my! / factor, 0) > 55) continue
        const radius = factor > 1 ? factor : 0
        for (let dy = -radius; dy <= radius; dy++) for (let dx = -radius; dx <= radius; dx++) {
          const px = x * factor + dx, py = y * factor + dy
          const error = patchError(f, templates.minimap, px, py, mx!, my!)
          if (error > 43) continue
          const existing = maps.find(v => Math.abs(v.x - px) < 20 * mx! && Math.abs(v.y - py) < 15 * my!)
          if (!existing) maps.push({ x: px, y: py, sx: mx!, sy: my!, error })
          else if (error < existing.error) Object.assign(existing, { x: px, y: py, sx: mx!, sy: my!, error })
        }
      }
      searchCancelled(options)
      yield
    }
  }
  const validMaps = maps.filter(map => mapField(f, map))
  const mapSearchMs = performance.now() - mapStarted
  if (validMaps.length > 1) return { regions: {}, diagnostics: { reason: 'multiple-scenes', durationMs: performance.now() - started, candidates: validMaps.length } }
  if (validMaps[0]) result.regions.mapName = mapField(f, validMaps[0])
  let mesosSearchMs = 0, mesosTimedOut = false
  const mesosStarted = performance.now()
  const mesosExpired = () => performance.now() - mesosStarted >= (options.mesosBudgetMs ?? Infinity)
    || options.deadlineAt !== undefined && Date.now() >= options.deadlineAt
  if (metrics.includes('mesos') && options.mesosCandidate) {
    const verified = validateMxdcLayout(f, { regions: { mesos: options.mesosCandidate } }).regions.mesos
    if (verified) result.regions.mesos = verified
  } else if (metrics.includes('mesos')) coinSearch: for (let y = 0; y < f.height - 10; y++) {
    if (mesosExpired()) { mesosTimedOut = true; break }
    for (const run of [175, 205, 140].flatMap(threshold => lightRuns(f, y, threshold, 48, best ? Math.ceil(110 * sx) : 430))) {
      if (mesosExpired()) { mesosTimedOut = true; break coinSearch }
      const measuredX = run.width / 104
      if (best && Math.abs(measuredX - sx) > .06) continue
      const mx = best?.sx ?? measuredX
      for (const my of best ? Array.from({ length: 17 }, (_, i) => sy + (i % 2 ? -1 : 1) * Math.ceil(i / 2) * .015) : searchScales.map(v => v[1]!)) {
        if (mesosExpired()) { mesosTimedOut = true; break coinSearch }
        if (!border(f, run.x, y + 20 * my, run.width)) continue
        const coinError = refined(f, templates.coin, run.x - 16 * mx, y, mx, my)
        if (coinError > 48) continue
        const voucherError = refined(f, templates.voucher, run.x - 16 * mx, y + 20 * my, mx, my)
        if (voucherError > 48) continue
        const titleError = refined(f, templates.inventoryTitle, run.x - 18 * mx, y - 301 * my, mx, my, 4)
        if (titleError > 48) continue
        const error = Math.max(coinError, voucherError, titleError)
        const existing = coins.find(v => Math.abs(v.x - run.x) < 8 && Math.abs(v.y - y) < 8)
        if (!existing) coins.push({ x: run.x, y, sx: mx, sy: my, error })
        else if (error < existing.error) Object.assign(existing, { x: run.x, y, sx: mx, sy: my, error })
        if (error < 5) break
      }
    }
    yield
  }
  if (metrics.includes('mesos')) mesosSearchMs = performance.now() - mesosStarted
  // A truncated search cannot establish uniqueness. Coin ambiguity does not invalidate HUD items.
  const coin = !mesosTimedOut && coins.length === 1 ? coins[0] : undefined
  if (coin) {
    const rect = normalized(f, coin.x - coin.sx, coin.y, 105 * coin.sx, 14 * coin.sy)
    result.regions.mesos = { rect, textRect: rect, confidence: Math.max(.7, 1 - coin.error / 150) }
  }
  const supportRegions = { ...hint?.regions, ...result.regions }
  for (const metric of recognitionMetricKeys) if (!metrics.includes(metric)) delete result.regions[metric]
  const reasons: Partial<Record<RecognitionMetricKey, LayoutFailureReason>> = Object.fromEntries(metrics.filter(m => !result.regions[m]).map(m => [m, best && hudMetrics.includes(m as typeof hudMetrics[number]) ? 'occluded' : 'not-found']))
  if (mesosTimedOut) reasons.mesos = 'timeout'
  else if (coins.length > 1) reasons.mesos = 'multiple-scenes'
  return { ...result, supportRegions, diagnostics: { scaleX: best?.sx ?? validMaps[0]?.sx ?? coin?.sx, scaleY: best?.sy ?? validMaps[0]?.sy ?? coin?.sy, reason: Object.keys(result.regions).length ? undefined : undersized ? 'too-small' : 'not-found', reasons, durationMs: performance.now() - started, hudSearchMs, mapSearchMs, mesosSearchMs, candidates: hud.length + validMaps.length + coins.length, anchors: hud.length * 2 + validMaps.length * 2 + (result.regions.mesos ? 3 : 0) } }
}
export function locateMxdcLayout(frame: ImageData, metrics: readonly RecognitionMetricKey[] = recognitionMetricKeys, options: LayoutSearchOptions = {}): LayoutDetection {
  const search = layoutSearch(frame, metrics, options)
  let step = search.next()
  while (!step.done) step = search.next()
  return step.value
}

/** Fresh-frame check at the persistence boundary; never starts another full-frame search. */
export function validateMxdcLayout(frame: ImageData, detection: LayoutDetection): LayoutDetection {
  const regions = { ...detection.supportRegions, ...detection.regions }
  const invalid = invalidMxdcPositions(frame, frame.width, frame.height, { hudRect: detection.hud, autoRois: regions, roiOverrides: {} } as CaptureProfile)
  for (const metric of invalid) delete regions[metric]
  const index = (['hp', 'mp', 'exp'] as const).findIndex(metric => regions[metric]?.barRect)
  if (index >= 0) {
    const region = regions[(['hp', 'mp', 'exp'] as const)[index]!]!
    const fields = hudFields(frame, regionAnchor(region, index, frame.width, frame.height))
    const enoughAnchors = ['hp', 'mp', 'exp'].filter(metric => fields.regions[metric as RecognitionMetricKey]).length >= 2
    for (const metric of hudMetrics) if (!enoughAnchors || !fields.regions[metric]
      || (regions[metric]?.hudVariant ?? 'wide') !== (region.hudVariant ?? 'wide')) delete regions[metric]
  } else for (const metric of hudMetrics) delete regions[metric]
  const coin = regions.mesos?.rect
  if (coin) {
    const sx = coin.width * frame.width / 105, sy = coin.height * frame.height / 14
    const x = coin.x * frame.width + sx, y = coin.y * frame.height
    if (refined(frame, templates.coin, x - 16 * sx, y, sx, sy, 2) > 50
      || refined(frame, templates.voucher, x - 16 * sx, y + 20 * sy, sx, sy, 2) > 50
      || refined(frame, templates.inventoryTitle, x - 18 * sx, y - 301 * sy, sx, sy, 4) > 50) delete regions.mesos
  }
  return { ...detection, supportRegions: regions, regions: Object.fromEntries(
    Object.keys(detection.regions).filter(metric => regions[metric as RecognitionMetricKey])
      .map(metric => [metric, regions[metric as RecognitionMetricKey]])) }
}

/** Local verification uses the same resampled anchors and never changes stored positions. */
export function invalidMxdcPositions(source: CanvasImageSource | ImageData, width: number, height: number, profile: CaptureProfile): RecognitionMetricKey[] {
  const invalid = new Set<RecognitionMetricKey>()
  const check = (t: Template, x: number, y: number, sx: number, sy: number) => {
    if (isImageDataSource(source)) return refined(source, t, x, y, sx, sy, 2) < 50
    const px = Math.floor(x - 3), py = Math.floor(y - 3), w = Math.ceil(t.width * sx + 7), h = Math.ceil(t.height * sy + 7)
    if (px < 0 || py < 0 || px + w > width || py + h > height) return false
    const image = drawSourceToImageData(source, width, height, { x: px / width, y: py / height, width: w / width, height: h / height })
    return refined(image, t, x - px, y - py, sx, sy, 2) < 50
  }
  let validHud = 0, testedHud = 0
  for (const [i, metric] of (['hp', 'mp', 'exp'] as const).entries()) {
    const region = profile.autoRois?.[metric], bar = region?.barRect
    if (!bar) continue
    testedHud++
    const a = regionAnchor(region!, i, width, height), { sx, sy } = a
    const compact = region!.hudVariant === 'compact'
    let valid = check((compact ? compactLabels : labels)[i]!, bar.x * width - (compact ? 2 : 1) * sx, bar.y * height - 15 * sy, sx, sy)
    if (valid && compact) {
      if (isImageDataSource(source)) valid = compactAnchorMatches(source, a, i, 50)
      else {
        const bounds = region!.rect
        const image = drawSourceToImageData(source, width, height, bounds)
        valid = compactAnchorMatches(image, { ...a, x: a.x - Math.round(bounds.x * width), y: a.y - Math.round(bounds.y * height) }, i, 50)
      }
    }
    if (valid) validHud++
    else if (!profile.roiOverrides[metric]) invalid.add(metric)
  }
  if (testedHud && validHud < Math.min(2, testedHud)) for (const metric of hudMetrics) invalid.add(metric)
  const map = profile.autoRois?.mapName?.rect
  if (map && !profile.roiOverrides.mapName) {
    const index = (['hp', 'mp', 'exp'] as const).findIndex(metric => profile.autoRois?.[metric]?.barRect)
    const sy = map.height * height / 18, sx = index >= 0
      ? regionAnchor(profile.autoRois![(['hp', 'mp', 'exp'] as const)[index]!]!, index, width, height).sx : sy
    if (!check(templates.minimap, map.x * width - 43 * sx, map.y * height - 39 * sy, sx, sy)) invalid.add('mapName')
  }
  return [...invalid]
}
